import React, { useEffect, useRef, useState } from "react";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Modal } from "./BulkModals";
import { DeletePlanRows, DeleteOptions, DeleteJobResults } from "./DeleteSitesBits";

const DEFAULT_OPTS = { email_providers: true, cloudflare: true, registrar: true, r2: true };

/** Bulk "Delete & tear down": dry-run plan → options + typed confirm → background job with per-step results. */
export default function DeleteSitesModal({ siteIds, onClose, onDone }) {
  const [plan, setPlan] = useState(null);
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [ackLive, setAckLive] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const timer = useRef(null);
  const finished = useRef(false);
  // Snapshot the selection at mount: the Sites list re-renders (20s readiness poll)
  // and clears its selection on done — neither may re-fire the plan or stop polling.
  const ids = useRef(siteIds).current;

  useEffect(() => {
    let alive = true;
    api.post("/bulk/delete-sites", { site_ids: ids, dry_run: true })
      .then((r) => { if (alive) setPlan(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not load the plan"); });
    return () => { alive = false; clearInterval(timer.current); };
  }, [ids]);

  function poll(id) {
    let misses = 0;
    timer.current = setInterval(async () => {
      try {
        const r = await api.get(`/bulk/jobs/${id}`);
        misses = 0;
        setJob(r.data);
        if (r.data.status !== "running" && !finished.current) {
          finished.current = true;
          clearInterval(timer.current);
          await onDone();
        }
      } catch (e) {
        // Transient poll failures must not freeze the progress view — the job keeps running server-side.
        if (++misses >= 10) { clearInterval(timer.current); setErr(e?.response?.data?.detail || "Lost contact with the job — it continues in the background; reload the Sites list."); }
      }
    }, 1500);
  }

  async function run() {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/bulk/delete-sites", { site_ids: ids, dry_run: false, confirm, acknowledge_live: ackLive, ...opts });
      setJob({ id: r.data.job_id, status: "running", total: r.data.total, done: 0, results: [] });
      poll(r.data.job_id);
    } catch (e) { setErr(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  }

  const liveCount = plan?.live || 0;
  const canRun = !!plan && !busy && confirm.trim() === (plan?.confirm_word || "DELETE") && (liveCount === 0 || ackLive);
  const n = ids.length;

  return (
    <Modal testId="bulk-delete-modal" title="Delete & tear down" subtitle={`${n} site${n === 1 ? "" : "s"} — removes the site and everything registered for its domain`} onClose={onClose}>
      {!plan && !err && <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="bulk-delete-loading"><Loader2 size={12} className="animate-spin" /> Building the teardown plan…</div>}

      {plan && !job && (
        <div className="space-y-4">
          <DeletePlanRows targets={plan.targets} />
          <DeleteOptions opts={opts} setOpts={setOpts} />
          {liveCount > 0 && (
            <label className="flex items-start gap-2 text-sm text-amber-200 border border-amber-500/40 bg-amber-500/10 px-3 py-2 rounded-sm cursor-pointer" data-testid="bulk-delete-live-warning">
              <input type="checkbox" checked={ackLive} onChange={(e) => setAckLive(e.target.checked)} data-testid="bulk-delete-ack-live" className="mt-0.5" />
              <span><AlertTriangle size={13} className="inline mr-1 -mt-0.5" />{liveCount} site{liveCount === 1 ? " has" : "s have"} more than {plan.live_threshold} published articles. I know this deletes live sites.</span>
            </label>
          )}
          <div>
            <div className="label-tiny mb-1">Type <span className="mono text-rose-300">{plan.confirm_word}</span> to confirm</div>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="bulk-delete-confirm-input" placeholder={plan.confirm_word} autoComplete="off" className="w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono focus:border-rose-500" />
          </div>
          <div className="text-[11px] text-zinc-500 leading-relaxed">Registrations are never cancelled by any registrar API — paid domains stay yours until expiry. Cloudflare Registrar auto-renew is switched off; Namecheap domains are listed for manual action.</div>
          <button onClick={run} disabled={!canRun} data-testid="bulk-delete-run" className="inline-flex items-center justify-center gap-1.5 w-full bg-rose-600/20 border border-rose-500/50 text-rose-200 hover:bg-rose-600/30 text-xs font-semibold uppercase tracking-wider px-3 py-2.5 rounded-sm disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete {n} site{n === 1 ? "" : "s"} & tear down
          </button>
        </div>
      )}

      {job && <DeleteJobResults job={job} />}
      {err && <div className="mt-3 text-xs text-rose-400 mono" data-testid="bulk-delete-error">{err}</div>}
    </Modal>
  );
}
